import {useCallback} from 'react';
import type {Lock} from 'services/locks/types';
import {useLocalization} from 'localization';
import {useUser} from 'services/auth';
import {useAcquireLockMutation} from 'services/locks';
import {useSnacks} from 'components/snacks';
import {isLockedByOtherUser} from './isLockedByOtherUser';

type AcquireLock = (currentLock: Lock | undefined) => Promise<Lock | undefined>;
/**
 * Acquires lock held by other user
 */

export const useAcquireLockWithErrorHandling = (): AcquireLock => {
  const {t} = useLocalization();
  const user = useUser();
  const {errorSnack} = useSnacks();
  const [acquireLock] = useAcquireLockMutation();

  return useCallback(
    (currentLock: Lock | undefined) => {
      if (currentLock && user && isLockedByOtherUser(currentLock, user)) {
        return acquireLock(currentLock)
          .unwrap()
          .then(lock => {
            return lock;
          })
          .catch(() => {
            // If unlock fails, still allow editing with warning
            errorSnack(t('page.metadata.lock.message.lockFailed'));
            return undefined;
          });
      }

      return Promise.resolve(undefined);
    },
    [acquireLock, errorSnack, t, user],
  );
};
